import React, {useEffect, useState} from 'react';
import SolidButton from "./form/SolidButton";
import PhoneInput, {Value as PhoneValue} from 'react-phone-number-input/input';
import {Alert, Box, Snackbar} from '@mui/material';
import {removeInitialSpecialChars} from "../util/sanitizeUtil";
import {styled} from "@mui/material/styles";

const CopyToClipBoard = styled('input')(() => ({
    '&:hover': {
        cursor: 'pointer',
    }
}));

const LocationManagerContent = styled('p')(() => ({
    marginTop: ".5em",
    marginBottom: ".5em"
}));

/**
 * Subset of `LocationManager` fields editable via this form. Matches the
 * shape of `updatedManagerInfo` state maintained by the parent
 * `LocationManagerContent` view.
 */
interface LocationManagerInfo {
    username: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    isOptedIntoText: boolean;
}

interface AddLocationManagerInfoProps {
    updatedManagerInfo: LocationManagerInfo;
    setUpdatedManagerInfo: React.Dispatch<React.SetStateAction<LocationManagerInfo>>;
    submit: () => void;
    showAllUpdateFields: boolean;
    showUpdateButton: boolean;
}

const AddLocationManagerInfo = (props: AddLocationManagerInfoProps) => {
    const {
        updatedManagerInfo,
        setUpdatedManagerInfo,
        submit,
        showAllUpdateFields,
        showUpdateButton,
    } = props;

    const [showTextOptInMessage, setShowTextOptInMessage] = useState(false);
    const [showEmailCopiedAlert, setShowEmailCopiedAlert] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const {name, value, type, checked} = e.target;
        setUpdatedManagerInfo((prevManagerInfo) => ({...prevManagerInfo, [name]: type !== 'checkbox' ? removeInitialSpecialChars(value) : checked}))
    }

    const handlePhoneNumberChange = (value?: PhoneValue): void => {
        setUpdatedManagerInfo((prevManagerInfo) => ({...prevManagerInfo, phoneNumber: value || ''}));
    }

    const handleEmailClick = (): void => {
        void window.navigator.clipboard.writeText(updatedManagerInfo.username);
        setShowEmailCopiedAlert(true);
    };

    useEffect(() => {
        setShowTextOptInMessage(updatedManagerInfo.isOptedIntoText);
    }, [updatedManagerInfo])

    return (
      <>
          {
            showAllUpdateFields && <>
                <fieldset className={'ss-top hs-field'}>
                    <label htmlFor="updateLocationManagerFirstName">LOCATION MANAGER FIRST NAME</label>
                    <input type="text"
                           id="updateLocationManagerFirstName"
                           name="firstName"
                           value={updatedManagerInfo?.firstName || ''}
                           onChange={handleChange}
                           placeholder="Enter First Name"
                    />
                </fieldset>
                <fieldset className="hs-field">
                    <label htmlFor="updateLocationManagerLastName">LOCATION MANAGER LAST NAME</label>
                    <input type="text"
                           id="updateLocationManagerLastName"
                           name="lastName"
                           value={updatedManagerInfo?.lastName || ''}
                           onChange={handleChange}
                           placeholder="Enter Last Name"
                    />
                </fieldset>
                <fieldset className="hs-field">
                    <label htmlFor="locationManagerPhoneNumber">LOCATION MANAGER PHONE NUMBER</label>
                    <PhoneInput
                      id="locationManagerPhoneNumber"
                      placeholder="Enter phone number"
                      defaultCountry="US"
                      name="phoneNumber"
                      value={updatedManagerInfo?.phoneNumber || ''}
                      maxLength={14}
                      onChange={handlePhoneNumberChange}
                    />
                </fieldset>
                <fieldset className="hs-field">
                    <label htmlFor="locationManagerEmail">LOCATION MANAGER EMAIL</label>
                    <CopyToClipBoard
                      id="locationManagerEmail"
                      name="email"
                      value={updatedManagerInfo?.username || ''}
                      readOnly
                      onClick={handleEmailClick}
                    />
                </fieldset>
            </>
          }
          {
              showTextOptInMessage ?
                <Box mb={2.2}>
                    <Alert severity="warning">
                        <LocationManagerContent>
                            Checking this box acknowledges that the location manager will be signed up to receive text message notifications for booking requests.
                        </LocationManagerContent>
                        <LocationManagerContent>
                            The user will receive an initial message in which they can approve or decline this feature.
                        </LocationManagerContent>
                        <LocationManagerContent>
                            If the user opts out, then to opt in again user must text 1-855-997-1771 with the word 'subscribe'.
                        </LocationManagerContent>
                    </Alert>
                </Box>
                :
                ""
          }
              <label className={'ss-checkbox'}>
                  <input type={'checkbox'}
                         name={'isOptedIntoText'}
                         className={'ss-checkbox-container-checkbox'}
                         checked={updatedManagerInfo.isOptedIntoText}
                         onChange={handleChange}
                  />
                  <span>Text Opt In</span>
              </label>
          {
            showUpdateButton && <SolidButton label={'Update'} className={'w-100'} onClick={submit}/>
          }
        <Snackbar
            open={showEmailCopiedAlert}
            autoHideDuration={4000}
            onClose={() => setShowEmailCopiedAlert(false)}
        >
            <Alert severity="info">Email copied to clipboard</Alert>
        </Snackbar>
      </>
    )
}

export default AddLocationManagerInfo;
